import { ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
} from '@bice_vida/ds-components-accordion';
import { Alert, AlertContent, AlertIcon } from '@bice_vida/ds-components-alert';
import {
  FullModal,
  FullModalCloseButton,
  FullModalContent,
  FullModalContentDescription,
  FullModalContentTitle,
} from '@bice_vida/ds-components-full-modal';
import FullModalAside from '@components/FullModalAside';
import { Table } from '@components/Table';
import useStore from '@hooks/useStore';
import { currencyFormat } from '@segurodeceso/core/utils/currency';
import { useCurrencyStore } from '@stores';

import styles from './FullModalAccordion.module.scss';

type TableT = {
  columns: { key: string; label: string }[];
  data: { [key: string]: string | number }[];
};

type TextT = {
  text: string;
  variables?: { [key: string]: number };
};

type AccordionT = {
  title: string;
  description?: string | TextT;
  table?: TableT;
  alert?: string;
};

interface FullModalAccordionProps {
  open: boolean;
  onClose: () => void;
  i18key: string;
  lowestPrice: number;
}

const FullModalAccordion = ({
  open,
  onClose,
  i18key,
  lowestPrice,
}: FullModalAccordionProps): ReactElement => {
  const { t } = useTranslation();
  const currencyStore = useStore(useCurrencyStore, (state) => state);
  const UFValue = currencyStore?.currency?.UFValue;
  const accordions: AccordionT[] = Array.isArray(t(`${i18key}.accordions`, { returnObjects: true }))
    ? (t(`${i18key}.accordions`, { returnObjects: true }) as AccordionT[])
    : [];

  return (
    <FullModal open={open} onClose={onClose}>
      <FullModalAside
        title={t(`${i18key}.asideTitle`)}
        subtitle={
          lowestPrice
            ? t(`${i18key}.asideSubtitle`, {
                uf_value: currencyFormat(lowestPrice, 'UF'),
                clp_value: currencyFormat(lowestPrice * UFValue, 'CLP'),
              })
            : ''
        }
        avatar="thinking"
      />

      <FullModalCloseButton style={{ zIndex: 10 }} />

      <FullModalContent>
        <FullModalContentTitle>{t(`${i18key}.title`)}</FullModalContentTitle>

        <FullModalContentDescription className="max-w-[670px]">
          <Trans t={t} i18nKey={[`${i18key}.description.text`, `${i18key}.description`]} />
        </FullModalContentDescription>

        <div className="mt-10 max-w-[670px]">
          <Accordion maxWidth type="single" variant="underline" collapsible>
            {accordions &&
              accordions?.map((accordion, index) => (
                <AccordionItem
                  value={`Accordion ${index}`}
                  key={'accordion-' + index}
                  className={styles.accordionItem}
                >
                  <AccordionHeader className={styles.accordionHeader}>
                    {accordion.title}
                  </AccordionHeader>
                  <AccordionContent>
                    {accordion.description && (
                      <Trans
                        t={t}
                        i18nKey={[
                          `${i18key}.accordions.${index}.description.text`,
                          `${i18key}.accordions.${index}.description`,
                        ]}
                        components={{ span: <span className="font-semibold" /> }}
                        values={{
                          clp_value:
                            typeof accordion.description !== 'string'
                              ? currencyFormat(
                                  accordion.description.variables?.uf_value * UFValue,
                                  'CLP'
                                )
                              : 0,
                        }}
                      />
                    )}

                    {accordion.table && accordion.table?.columns && accordion.table?.data && (
                      <div className="`w-[640px] overflow-auto">
                        <Table
                          columns={accordion.table.columns}
                          data={accordion.table.data}
                          intercalatedColors
                          centered
                          scrollable
                          className={`w-[638px] md:w-full ${accordion.description ? 'mt-4' : ''}`}
                        />
                      </div>
                    )}

                    {accordion.alert && (
                      <Alert variant="warning" className="mt-4">
                        <AlertIcon />
                        <AlertContent>
                          <Trans
                            t={t}
                            i18nKey={`${i18key}.accordions.${index}.alert`}
                            components={{ span: <span className="font-semibold" />, br: <br /> }}
                          />
                        </AlertContent>
                      </Alert>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
          </Accordion>
        </div>
      </FullModalContent>
    </FullModal>
  );
};

export default FullModalAccordion;
